import i18next from 'i18next';

import { CustomerData, CustomerDetailsDto } from 'types/api/customer.type';
import { OrderHeaderResponse, OrderItem } from 'types/api/orders.type';

import { customersDataService, ordersDataService } from 'services';
import { pdfService } from 'services/pdf.service';

import { buildOrderPdfPayload } from 'helpers/buildOrderPdfPayload';

type CustomerInfoSubset = {
  code?: string;
  companyName?: string;
  address?: string;
  postCode?: string;
  city?: string;
  province?: string;
  country?: string;
  bankName?: string;
  contactPerson?: string;
  email?: string;
  provinceCode?: string;
  paymentCondition?: string;
};

type Translated = { descriptionIt?: string; descriptionEn?: string };

const pickDescription = (item: Translated) =>
  (i18next.language?.startsWith('it') ? item.descriptionIt : item.descriptionEn) ||
  item.descriptionEn ||
  item.descriptionIt ||
  '';

/** Customer block of the PDF from `GET /customers/{id}/details` — same values as the order page. */
const customerInfoFromDetails = (d: CustomerDetailsDto): CustomerInfoSubset => ({
  code: d.customerCode,
  companyName: d.displayName,
  address: d.billingAddress?.addressLine ?? '',
  postCode: d.billingAddress?.zipCode ?? '',
  city: d.billingAddress?.city ?? '',
  province: d.billingAddress?.subdivisionName ?? '',
  provinceCode: d.billingAddress?.subdivisionCode ?? '',
  country: d.billingAddress?.countryName ?? '',
  bankName:
    [d.paymentType?.descriptionIt, d.paymentType?.descriptionEn].filter(Boolean).join(' / ') ||
    d.paymentType?.code ||
    '',
  paymentCondition: d.paymentType
    ? [d.paymentType.code, pickDescription(d.paymentType)].filter(Boolean).join(' - ')
    : '',
  contactPerson: d.contactPerson ?? '',
  email: d.email ?? '',
});

const extractCustomerInfo = (customer: CustomerData): CustomerInfoSubset => ({
  code: customer.code,
  companyName: customer.companyName,
  address: customer.address,
  postCode: customer.postCode,
  city: customer.city,
  province: customer.province,
  country: customer.country,
  bankName: customer.bankName,
  contactPerson: customer.contactPerson,
  email: customer.email,
});

type DownloadOrderPdfParams = {
  orderId: string;
  /** Pre-loaded header — if provided, skips the getOrderById call. */
  header?: OrderHeaderResponse;
  /** Pre-loaded items — falls back to header.items if not provided. */
  items?: OrderItem[];
  /** Pre-loaded customer info — if provided, skips customer lookup. */
  customerInfo?: CustomerInfoSubset | CustomerData;
  /** Customer numeric ID — used to fetch addresses when customerInfo is pre-loaded. */
  customerId?: number;
  /** Pre-loaded customer details (order page) — preferred over `customerInfo`. */
  customerDetails?: CustomerDetailsDto;
  /** Selected price type for the PDF. Undefined means "No Price". */
  priceType?: 'DISTRIBUTOR' | 'WHOLESALE' | 'RETAIL';
};

/**
 * Resolves the numeric customer ID and customer info for the PDF.
 *
 * Uses the same lookup strategy as the Order page:
 * searchCustomers → getDetails → extract customer info.
 */
const resolveCustomer = async (
  customerCode: string
): Promise<{ customerInfo: CustomerInfoSubset; customerId: number } | null> => {
  const searchRes = await customersDataService.searchCustomers(customerCode);
  const rows = searchRes.data.content ?? [];
  const exact = rows.find((r) => r.customerCode === customerCode) ?? rows[0];

  if (!exact) {
    console.warn('[downloadOrderPdf] Customer not found via search:', customerCode);
    return null;
  }

  const detailsRes = await customersDataService.getDetails(exact.id);

  return {
    customerId: exact.id,
    customerInfo: customerInfoFromDetails(detailsRes.data),
  };
};

/**
 * Fetches all data needed for the order PDF (header, customer, addresses)
 * and triggers the file download.
 *
 * Used by both the Order page Actions and the Order List Actions to ensure
 * consistent PDF output with full address information.
 */
const downloadOrderPdf = async ({
  orderId,
  header: preloadedHeader,
  items: preloadedItems,
  customerInfo: preloadedCustomerInfo,
  customerId: preloadedCustomerId,
  customerDetails: preloadedCustomerDetails,
  priceType,
}: DownloadOrderPdfParams): Promise<void> => {
  // 1. Get order header
  const header = preloadedHeader ?? (await ordersDataService.getOrderById(orderId)).data;
  const items = preloadedItems ?? header.items ?? [];

  // 2. Resolve customer info and numeric ID
  let customerInfo: CustomerInfoSubset | undefined;
  let customerId: number | undefined = preloadedCustomerId;

  if (preloadedCustomerDetails) {
    customerInfo = customerInfoFromDetails(preloadedCustomerDetails);
    customerId = customerId ?? preloadedCustomerDetails.customerId;
  } else if (preloadedCustomerInfo) {
    customerInfo =
      'id' in preloadedCustomerInfo
        ? extractCustomerInfo(preloadedCustomerInfo as CustomerData)
        : preloadedCustomerInfo;
    if (!customerId && 'id' in preloadedCustomerInfo) {
      customerId = Number((preloadedCustomerInfo as CustomerData).id) || undefined;
    }
  } else if (header.customerCode) {
    // Fetch customer details — don't swallow errors silently so we can debug.
    const resolved = await resolveCustomer(header.customerCode);
    if (resolved) {
      customerInfo = resolved.customerInfo;
      customerId = resolved.customerId;
    }
  }

  // 3. Fetch shipping & goods destination addresses
  let shippingAddress = null;
  let goodsDestination = null;

  if (customerId) {
    const [shippingRes, goodsRes] = await Promise.all([
      customersDataService.getAddresses(customerId, 'SHIPPING'),
      customersDataService.getAddresses(customerId, 'GOODS_DESTINATION'),
    ]);

    const shippingList = shippingRes.data ?? [];
    const goodsList = goodsRes.data ?? [];

    if (header.shippingAddressCode) {
      shippingAddress =
        shippingList.find((a) => a.code === header.shippingAddressCode) ?? null;
    }
    // Fallback to primary/first address when no specific code is stored
    if (!shippingAddress && shippingList.length > 0) {
      shippingAddress = shippingList.find((a) => a.isPrimary) ?? shippingList[0];
    }

    if (header.goodsDestinationCode) {
      goodsDestination = goodsList.find((a) => a.code === header.goodsDestinationCode) ?? null;
    }
    // Fallback to primary/first address when no specific code is stored
    if (!goodsDestination && goodsList.length > 0) {
      goodsDestination = goodsList.find((a) => a.isPrimary) ?? goodsList[0];
    }
  }

  // 4. Build payload and request download URL
  const payload = buildOrderPdfPayload({
    header,
    items,
    customerInfo,
    shippingAddress,
    goodsDestination,
    priceType,
  });

  // Pass priceType to Lambda so it can include/exclude price columns
  if (priceType) {
    (payload as Record<string, any>).priceType = priceType; // eslint-disable-line
  }

  const pdfRes = await pdfService.getOrderPDF(payload);
  const { downloadUrl } = pdfRes.data;

  // The presigned S3 URL already has Content-Disposition: attachment set,
  // so the browser will download the file directly without opening it in a tab.
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export { downloadOrderPdf };
export type { CustomerInfoSubset, DownloadOrderPdfParams };
